"use client";

import { useEffect, useRef } from "react";

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

const generateParticles = (count: number, minSize: number, maxSize: number): Particle[] => {
  return Array.from({ length: count }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: minSize + Math.random() * (maxSize - minSize),
    delay: Math.random() * 5,
    duration: 2.6 + Math.random() * 8,
  }));
};

const generateShootingStars = (count: number): Particle[] => {
  return Array.from({ length: count }).map((_, i) => ({
    id: i,
    x: 50 + Math.random() * 50,
    y: Math.random() * 40,
    size: 0,
    delay: Math.random() * 15,
    duration: 10 + Math.random() * 10,
  }));
};

export default function BackgroundSpice() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = window.innerWidth;
    let height = window.innerHeight;

    const setCanvasSize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    setCanvasSize();
    window.addEventListener("resize", setCanvasSize);

    const stars = generateParticles(24, 1.5, 2.5);
    const dust = generateParticles(53, 1.2, 2);
    const shootingStars = generateShootingStars(7);

    const render = () => {
      ctx.clearRect(0, 0, width, height);
      const time = performance.now() / 1000;

      dust.forEach((d) => {
        const phase = (Math.sin((time * Math.PI) / d.duration + d.delay) + 1) / 2;
        const currentSize = (d.size * (0.5 + 0.5 * phase)) / 2;
        const opacity = 0.4 * phase;

        ctx.fillStyle = `rgba(199, 210, 254, ${opacity})`;
        ctx.beginPath();
        ctx.arc((d.x * width) / 100, (d.y * height) / 100, currentSize, 0, Math.PI * 2);
        ctx.fill();
      });

      stars.forEach((star) => {
        const phase = (Math.sin((time * Math.PI) / star.duration + star.delay) + 1) / 2;
        const currentSize = (star.size * (0.8 + 0.4 * phase)) / 2;
        const opacity = 0.1 + 0.6 * phase;
        const px = (star.x * width) / 100;
        const py = (star.y * height) / 100;

        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.beginPath();
        ctx.arc(px, py, currentSize, 0, Math.PI * 2);
        ctx.fill();

        if (phase > 0.5) {
          const glowOpacity = (phase - 0.5) * 2 * 0.3;
          const glowRadius = currentSize * 4;
          const gradient = ctx.createRadialGradient(px, py, currentSize, px, py, glowRadius);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${glowOpacity})`);
          gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(px, py, glowRadius, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      shootingStars.forEach((star) => {
        const t = ((time + star.delay) % star.duration) / star.duration;
        if (t < 0.15) {
          let progress, dist, scale, opacity;
          if (t < 0.05) {
            progress = t / 0.05;
            dist = progress * 50;
            scale = progress;
            opacity = progress;
          } else {
            progress = (t - 0.05) / 0.10;
            dist = 50 + progress * 550;
            scale = 1 - progress;
            opacity = 1 - progress;
          }

          const px = (star.x * width) / 100 - dist;
          const py = (star.y * height) / 100 + dist;

          ctx.save();
          ctx.translate(px, py);
          ctx.rotate((3 * Math.PI) / 4);

          const tailLength = 100 * scale;

          const grad = ctx.createLinearGradient(-tailLength, 0, 0, 0);
          grad.addColorStop(0, `rgba(199, 210, 254, 0)`);
          grad.addColorStop(0.5, `rgba(199, 210, 254, ${opacity})`);
          grad.addColorStop(1, `rgba(255, 255, 255, ${opacity})`);

          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.ellipse(-tailLength / 2, 0, tailLength / 2, 0.75 * scale, 0, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
          ctx.beginPath();
          ctx.arc(0, 0, 1.5 * scale, 0, Math.PI * 2);
          ctx.fill();

          const glowRadius = 10 * scale;
          const glowGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRadius);
          glowGrad.addColorStop(0, `rgba(255, 255, 255, ${opacity * 0.8})`);
          glowGrad.addColorStop(1, `rgba(255, 255, 255, 0)`);
          ctx.fillStyle = glowGrad;
          ctx.beginPath();
          ctx.arc(0, 0, glowRadius, 0, Math.PI * 2);
          ctx.fill();

          ctx.restore();
        }
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", setCanvasSize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 w-full h-full pointer-events-none bg-transparent"
    />
  );
}